<template>
    <div
        class="mb-12 overflow-hidden rounded-xl bg-slate-50 p-3 dark:bg-slate-800/25"
    >
        <div class="w-full">
            <div class="mx-auto w-full rounded-2xl">
                <div class="flex justify-between">
                    <h3
                        class="mt-1 text-lg font-medium leading-6 dark:text-white"
                    >
                        Configurações Financeiras
                    </h3>
                    <button
                        v-if="!audit"
                        :disabled="loading"
                        type="button"
                        class="mt-3 inline-flex w-full items-center justify-center rounded-md border border-indigo-500 bg-indigo-500 px-4 py-1 text-base font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-indigo-100 sm:mt-0 sm:w-auto sm:text-sm"
                        @click="openModal()"
                    >
                        <font-awesome-icon
                            icon="fa-solid fa-cog"
                            class="mr-1 dark:text-white"
                            aria-hidden="true"
                        />
                        Editar
                    </button>
                </div>

                <ticto-loading v-if="loading" />
                <div v-else class="mt-4 space-y-4">
                    <!-- Alerta de auditoria -->
                    <div
                        v-if="audit"
                        class="rounded-lg border-l-4 border-yellow-500 bg-yellow-50 p-4"
                        role="alert"
                    >
                        <div class="flex items-center">
                            <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="mr-2 text-yellow-600" />
                            <p class="text-sm font-medium text-yellow-700">
                                A auditoria está em andamento para este
                                usuário. Alterações na configuração
                                permanecerão desativadas até a conclusão da
                                auditoria.
                            </p>
                        </div>
                    </div>

                    <!-- Gateways de Pagamento -->
                    <div v-if="!audit" class="overflow-hidden rounded-lg border border-gray-200">
                        <button
                            type="button"
                            class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                            @click="expandedGateways = !expandedGateways"
                        >
                            <div class="flex items-center space-x-2">
                                <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-100">
                                    <font-awesome-icon icon="fa-solid fa-wallet" class="text-xs text-indigo-600" />
                                </span>
                                <span class="text-sm font-semibold text-gray-700">Gateways de Pagamento</span>
                                <span class="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                    {{ gateways.length || 0 }} gateways
                                </span>
                            </div>
                            <font-awesome-icon
                                icon="fa-solid fa-chevron-down"
                                class="text-xs text-gray-400 transition-transform duration-200"
                                :class="{ 'rotate-180': expandedGateways }"
                            />
                        </button>
                        <div v-show="expandedGateways" class="border-t border-gray-200">
                            <form @submit.prevent="submitGateways" class="p-4">
                                <div class="space-y-3">
                                    <div
                                        v-for="(gateway, index) in gateways"
                                        :key="gateway.id"
                                        class="rounded-lg border border-gray-100 bg-gray-50/50 p-3"
                                    >
                                        <div class="grid grid-cols-1 gap-3 md:grid-cols-5">
                                            <div class="flex flex-col">
                                                <span class="text-xs font-medium text-gray-500">Gateway {{ index + 1 }}</span>
                                                <span class="mt-0.5 text-sm font-semibold capitalize text-gray-700">{{ gateway.name }}</span>
                                            </div>
                                            <div class="flex flex-col">
                                                <span class="text-xs font-medium text-gray-500">Prioridade (Nacional)</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    :max="gateways.length"
                                                    v-model="form.gateways[index].priority"
                                                    class="mt-1 w-20 rounded-lg border border-gray-300 bg-white p-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div class="flex flex-col">
                                                <span class="text-xs font-medium text-gray-500">Habilitado (Nacional)</span>
                                                <div class="mt-1">
                                                    <form-toggle v-model="form.gateways[index].is_active" />
                                                </div>
                                            </div>
                                            <div class="flex flex-col">
                                                <span class="text-xs font-medium text-gray-500">Prioridade (Internacional)</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    :max="gateways.length"
                                                    v-model="form.gateways[index].international_priority"
                                                    class="mt-1 w-20 rounded-lg border border-gray-300 bg-white p-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div class="flex flex-col">
                                                <span class="text-xs font-medium text-gray-500">Habilitado (Internacional)</span>
                                                <div class="mt-1">
                                                    <form-toggle v-model="form.gateways[index].is_international" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    :disabled="loading"
                                    type="submit"
                                    :class="{ 'cursor-not-allowed': loading }"
                                    class="mt-4 inline-flex items-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                                >
                                    Salvar Gateways
                                </button>
                            </form>
                        </div>
                    </div>

                    <!-- Taxas e Configurações Financeiras -->
                    <financial-methods-info :settings="settings" />

                    <!-- Faixas de Taxas -->
                    <fee-tiers-section v-if="hasPermission('manage.fee-tiers') || hasPermission('view.fee-tiers')" :user="user" />

                    <!-- Histórico -->
                    <UserFinancialLogs v-if="!loading" :user="user" />

                    <edit-financial-settings-modal
                        :settings="settings"
                        :user="user"
                        :open="edit_financial_modal"
                        v-if="edit_financial_modal"
                        @closeModal="closeModal()"
                        @closeModalAndFetch="closeAndFetchModal()"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useToast } from "vue-toastification";
import EditFinancialSettingsModal from "../modals/EditFinancialSettingsModal.vue";
import FeeTiersSection from "./FeeTiersSection.vue";
import FinancialMethodsInfo from "../../../components/layouts/FinancialMethodsInfo.vue";
import FormToggle from "../../../components/forms/FormToggle.vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import PermissionMixin from "../../../mixins/PermissionMixin.vue";
import TictoLoading from "../../../components/layouts/TictoLoading.vue";
import UserFinancialLogs from "./UserFinancialLogs.vue";
export default {
    props: ["user"],
    components: {
        TictoLoading,
        FeeTiersSection,
        FinancialMethodsInfo,
        EditFinancialSettingsModal,
        FormToggle,
        UserFinancialLogs,
    },
    mixins: [HelperMixin, PermissionMixin],

    data() {
        return {
            audit: false,
            loading: true,
            settings: {},
            gateways: {},
            gatewaysUser: {},
            form: {
                gateways: [],
            },
            edit_financial_modal: false,
            expandedGateways: false,
        };
    },

    setup() {
        const toast = useToast();
        return { toast };
    },

    mounted() {
        this.fetch();
        this.fetchGateways();
    },

    methods: {
        fetch() {
            this.loading = true;
            axios
                .get("/api/user/" + this.user.id + "/settings")
                .then(({ data }) => {
                    this.settings = data;
                })
                .finally(() => {
                    this.loading = false;
                });

            const params = {};
            params["filter[user_id]"] = this.$route.params.user_id;

            axios
                .get("/api/audit", {
                    params,
                })
                .then(({ data }) => {
                    this.audit = data.data.find((audit) => {
                        return audit.status === "open";
                    });
                });
        },
        fetchGateways() {
            this.loading = true;
            axios
                .get("/api/gateway")
                .then(({ data }) => {
                    this.gateways = data;

                    axios
                        .get("/api/user/" + this.user.id + "/gatewayUser")
                        .then((response) => {
                            this.gatewaysUser = response.data;

                            data?.map((gateway, i) => {
                                let gatewayUser = response.data.find((item) => {
                                    return item.gateway_id === gateway.id;
                                });

                                this.form.gateways[i] = {
                                    gateway_id: gateway.id,
                                    is_active:
                                        gatewayUser?.is_active ??
                                        gateway?.is_active,
                                    priority:
                                        gatewayUser?.priority ??
                                        gateway?.priority,
                                    is_international:
                                        gatewayUser?.is_international ??
                                        gateway?.is_international,
                                    international_priority:
                                        gatewayUser?.international_priority ??
                                        gateway?.international_priority,
                                };
                            });
                        })
                        .finally(() => {
                            this.loading = false;
                        });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        submitGateways() {
            this.loading = true;

            var noDuplicates = new Set(
                this.form.gateways?.map((gateway) => gateway.priority),
            );

            if (noDuplicates.size < this.form.gateways?.length) {
                this.toast.error("As prioridades não podem ser iguais");
                this.loading = false;

                return;
            }

            axios
                .post(
                    "/api/user/" + this.user.id + "/gateway",
                    this.form.gateways,
                )
                .then(({ data }) => {
                    this.toast.success("Configurações salvas com sucesso.");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        openModal() {
            this.edit_financial_modal = true;
        },
        closeModal() {
            this.edit_financial_modal = false;
        },
        closeAndFetchModal() {
            this.edit_financial_modal = false;
            this.fetch();
        },
    },
};
</script>
